import { useState, useEffect, useMemo } from 'react';
import {
  Wrench,
  Plus,
  ChevronDown,
  ChevronUp,
  Edit,
  Trash2,
  AlertCircle,
  Clock,
  CheckCircle,
  Calendar,
  History,
  X,
  Save,
  Loader2,
  Search
} from '../../icons/wp-icons';
import { anlagenApi } from '../../api/anlagenApi';

const INTERVALL_TYP_LABELS = {
  GESETZLICH: 'Gesetzlich',
  EMPFOHLEN: 'Empfohlen',
  VEREINBARUNG: 'Vereinbarung'
};

const ERGEBNIS_CONFIG = {
  OK: { label: 'OK', color: 'text-green-600 bg-green-50' },
  MAENGEL: { label: 'Mängel', color: 'text-yellow-600 bg-yellow-50' },
  REPARATUR_NOETIG: { label: 'Reparatur nötig', color: 'text-red-600 bg-red-50' }
};

function formatDatum(datum) {
  if (!datum) return '-';
  return new Date(datum).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

function getWartungStatus(vertrag) {
  if (!vertrag.naechsteWartungFaellig) return { status: 'unbekannt', label: 'Kein Datum', color: 'text-gray-500' };
  const heute = new Date();
  heute.setHours(0, 0, 0, 0);
  const faellig = new Date(vertrag.naechsteWartungFaellig);
  faellig.setHours(0, 0, 0, 0);
  const diffTage = Math.ceil((faellig - heute) / (1000 * 60 * 60 * 24));

  if (diffTage < 0) {
    return { status: 'ueberfaellig', label: `${Math.abs(diffTage)} Tage überfällig`, color: 'text-red-600', icon: AlertCircle, dot: 'bg-red-500' };
  } else if (diffTage <= 30) {
    return { status: 'bald', label: `In ${diffTage} Tagen fällig`, color: 'text-yellow-600', icon: Clock, dot: 'bg-yellow-500' };
  } else {
    return { status: 'ok', label: `Nächste: ${formatDatum(vertrag.naechsteWartungFaellig)}`, color: 'text-green-600', icon: CheckCircle, dot: 'bg-green-500' };
  }
}

// Anlage-Formular (inline)
function AnlageForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    bezeichnung: initial?.bezeichnung || '',
    typ: initial?.typ || '',
    hersteller: initial?.hersteller || '',
    modell: initial?.modell || '',
    seriennummer: initial?.seriennummer || '',
    baujahr: initial?.baujahr || '',
    standort: initial?.standort || '',
    notizen: initial?.notizen || ''
  });

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Bezeichnung *</label>
          <input type="text" value={form.bezeichnung} onChange={e => setForm(f => ({ ...f, bezeichnung: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary"
            placeholder="z.B. Viessmann Vitodens 200" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Typ</label>
          <input type="text" value={form.typ} onChange={e => setForm(f => ({ ...f, typ: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary"
            placeholder="z.B. Heizung, Klimaanlage" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Hersteller</label>
          <input type="text" value={form.hersteller} onChange={e => setForm(f => ({ ...f, hersteller: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Modell</label>
          <input type="text" value={form.modell} onChange={e => setForm(f => ({ ...f, modell: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Baujahr</label>
          <input type="number" value={form.baujahr} onChange={e => setForm(f => ({ ...f, baujahr: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary"
            placeholder="z.B. 2020" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Standort</label>
          <input type="text" value={form.standort} onChange={e => setForm(f => ({ ...f, standort: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary"
            placeholder="z.B. Keller, Dachgeschoss" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Seriennummer</label>
          <input type="text" value={form.seriennummer} onChange={e => setForm(f => ({ ...f, seriennummer: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Notizen</label>
        <textarea value={form.notizen} onChange={e => setForm(f => ({ ...f, notizen: e.target.value }))} rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary resize-none" />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition">
          Abbrechen
        </button>
        <button onClick={() => onSave(form)} disabled={!form.bezeichnung || saving}
          className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition disabled:opacity-50 flex items-center gap-1">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Speichern
        </button>
      </div>
    </div>
  );
}

// Wartungsvertrag-Formular (inline)
function WartungsVertragForm({ anlageId, initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    wartungsTyp: initial?.wartungsTyp || '',
    intervallMonate: initial?.intervallMonate || 12,
    intervallTyp: initial?.intervallTyp || 'EMPFOHLEN',
    letzteWartung: initial?.letzteWartung ? new Date(initial.letzteWartung).toISOString().split('T')[0] : '',
    erinnerungVorTagen: initial?.erinnerungVorTagen || 30,
    notizen: initial?.notizen || ''
  });

  return (
    <div className="bg-blue-50 rounded-lg p-3 space-y-3 mt-2">
      <p className="text-xs font-medium text-blue-700">
        {initial ? 'Wartungsvertrag bearbeiten' : 'Neuer Wartungsvertrag'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Wartungstyp *</label>
          <input type="text" value={form.wartungsTyp} onChange={e => setForm(f => ({ ...f, wartungsTyp: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary"
            placeholder="z.B. Heizungswartung, DGUV V3" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Intervall (Monate)</label>
          <input type="number" value={form.intervallMonate} onChange={e => setForm(f => ({ ...f, intervallMonate: parseInt(e.target.value) || 12 }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary"
            min={1} max={120} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Typ</label>
          <select value={form.intervallTyp} onChange={e => setForm(f => ({ ...f, intervallTyp: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary bg-white">
            <option value="GESETZLICH">Gesetzlich vorgeschrieben</option>
            <option value="EMPFOHLEN">Empfohlen</option>
            <option value="VEREINBARUNG">Vereinbarung</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Letzte Wartung</label>
          <input type="date" value={form.letzteWartung} onChange={e => setForm(f => ({ ...f, letzteWartung: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Erinnerung vor (Tagen)</label>
          <input type="number" value={form.erinnerungVorTagen} onChange={e => setForm(f => ({ ...f, erinnerungVorTagen: parseInt(e.target.value) || 30 }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-primary focus:border-primary"
            min={1} max={365} />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition">
          Abbrechen
        </button>
        <button onClick={() => onSave({ ...form, anlageId })} disabled={!form.wartungsTyp || saving}
          className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition disabled:opacity-50 flex items-center gap-1">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Speichern
        </button>
      </div>
    </div>
  );
}

function AnlagenSection({ kundeId, initialAnlagen, readOnly }) {
  const [anlagen, setAnlagen] = useState(initialAnlagen || []);
  const [expanded, setExpanded] = useState({});
  const [showAnlageForm, setShowAnlageForm] = useState(false); // 'new' or anlageId for edit
  const [showVertragForm, setShowVertragForm] = useState(null); // { anlageId, vertrag? }
  const [showHistorie, setShowHistorie] = useState(null); // anlageId
  const [historie, setHistorie] = useState({});
  const [historieLoading, setHistorieLoading] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);

  useEffect(() => {
    if (initialAnlagen) {
      setAnlagen(initialAnlagen);
    }
  }, [initialAnlagen]);

  const loadAnlagen = async () => {
    try {
      const res = await anlagenApi.getByKunde(kundeId);
      setAnlagen(res.data);
    } catch (error) {
      console.error('Fehler beim Laden der Anlagen:', error);
    }
  };

  const handleSaveAnlage = async (form) => {
    try {
      setSaving(true);
      if (showAnlageForm === 'new') {
        await anlagenApi.create({ ...form, kundeId });
      } else {
        await anlagenApi.update(showAnlageForm, form);
      }
      await loadAnlagen();
      setShowAnlageForm(false);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert(error.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAnlage = async (anlageId) => {
    if (!confirm('Anlage wirklich deaktivieren?')) return;
    try {
      await anlagenApi.delete(anlageId);
      await loadAnlagen();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
    }
  };

  const handleSaveVertrag = async (form) => {
    try {
      setSaving(true);
      if (showVertragForm.vertrag) {
        await anlagenApi.updateWartungsVertrag(showVertragForm.vertrag.id, form);
      } else {
        await anlagenApi.createWartungsVertrag(form);
      }
      await loadAnlagen();
      setShowVertragForm(null);
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert(error.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteVertrag = async (vertragId) => {
    if (!confirm('Wartungsvertrag wirklich deaktivieren?')) return;
    try {
      await anlagenApi.deleteWartungsVertrag(vertragId);
      await loadAnlagen();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
    }
  };

  const loadHistorie = async (anlageId) => {
    if (showHistorie === anlageId) {
      setShowHistorie(null);
      return;
    }
    try {
      setHistorieLoading(anlageId);
      const res = await anlagenApi.getHistorie(anlageId);
      setHistorie(prev => ({ ...prev, [anlageId]: res.data }));
      setShowHistorie(anlageId);
    } catch (error) {
      console.error('Fehler beim Laden der Historie:', error);
    } finally {
      setHistorieLoading(null);
    }
  };

  const toggleExpand = (anlageId) => {
    setExpanded(prev => ({ ...prev, [anlageId]: !prev[anlageId] }));
  };

  // Gesamtzahl fällige Wartungen
  const faelligeCount = anlagen.reduce((count, anlage) => {
    return count + (anlage.wartungsVertraege || []).filter(v => {
      if (!v.naechsteWartungFaellig) return false;
      const diff = new Date(v.naechsteWartungFaellig) - new Date();
      return diff < 30 * 24 * 60 * 60 * 1000;
    }).length;
  }, 0);

  const filteredAnlagen = useMemo(() => {
    let filtered = anlagen;

    // Status-Filter
    if (statusFilter === 'ueberfaellig') {
      filtered = filtered.filter(a =>
        (a.wartungsVertraege || []).some(v => getWartungStatus(v).status === 'ueberfaellig')
      );
    } else if (statusFilter === 'bald') {
      filtered = filtered.filter(a =>
        (a.wartungsVertraege || []).some(v => getWartungStatus(v).status === 'bald')
      );
    } else if (statusFilter === 'ok') {
      filtered = filtered.filter(a => {
        const vertraege = a.wartungsVertraege || [];
        return vertraege.length === 0 || vertraege.every(v => getWartungStatus(v).status === 'ok');
      });
    }

    // Textsuche
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(anlage => {
        if (anlage.bezeichnung?.toLowerCase().includes(searchLower)) return true;
        if (anlage.typ?.toLowerCase().includes(searchLower)) return true;
        if (anlage.hersteller?.toLowerCase().includes(searchLower)) return true;
        if (anlage.modell?.toLowerCase().includes(searchLower)) return true;
        if (anlage.standort?.toLowerCase().includes(searchLower)) return true;
        if (anlage.notizen?.toLowerCase().includes(searchLower)) return true;
        // Suche in Wartungsverträgen
        if ((anlage.wartungsVertraege || []).some(v => {
          if (v.wartungsTyp?.toLowerCase().includes(searchLower)) return true;
          if (INTERVALL_TYP_LABELS[v.intervallTyp]?.toLowerCase().includes(searchLower)) return true;
          return false;
        })) return true;
        return false;
      });
    }

    return filtered;
  }, [anlagen, searchTerm, statusFilter]);

  const hasActiveFilter = searchTerm.trim() || statusFilter;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
        <div className="flex items-center gap-2">
          <Wrench className="w-5 h-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">
            Anlagen & Wartung ({anlagen.length})
          </h3>
          {faelligeCount > 0 && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
              {faelligeCount} fällig
            </span>
          )}
        </div>
        {!readOnly && (
          <button
            onClick={() => setShowAnlageForm('new')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Neue Anlage</span>
          </button>
        )}
      </div>

      {/* Suchleiste für Anlagen */}
      {anlagen.length > 0 && (
        <div className="mb-4 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Anlagen durchsuchen..."
              className="w-full pl-10 pr-10 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex gap-1.5">
              {[
                { key: null, label: 'Alle' },
                { key: 'ueberfaellig', label: 'Überfällig', dotColor: 'bg-red-500' },
                { key: 'bald', label: 'Bald fällig', dotColor: 'bg-yellow-500' },
                { key: 'ok', label: 'OK', dotColor: 'bg-green-500' },
              ].map(({ key, label, dotColor }) => (
                <button
                  key={label}
                  onClick={() => setStatusFilter(statusFilter === key ? null : key)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition flex items-center gap-1.5 ${
                    statusFilter === key
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {dotColor && <span className={`w-1.5 h-1.5 rounded-full ${statusFilter === key ? 'bg-white' : dotColor}`} />}
                  {label}
                </button>
              ))}
            </div>
            {hasActiveFilter && (
              <p className="text-xs text-gray-500">
                {filteredAnlagen.length} von {anlagen.length} Anlagen
              </p>
            )}
          </div>
        </div>
      )}

      {/* Neue Anlage Formular */}
      {showAnlageForm === 'new' && (
        <div className="mb-4">
          <AnlageForm onSave={handleSaveAnlage} onCancel={() => setShowAnlageForm(false)} saving={saving} />
        </div>
      )}

      {/* Anlagen Liste */}
      {anlagen.length === 0 && !showAnlageForm ? (
        <div className="text-center py-8 text-gray-400">
          <Wrench className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Keine Anlagen hinterlegt</p>
          {!readOnly && (
            <p className="text-xs mt-1">Legen Sie Anlagen an, um Wartungsverträge zu verwalten</p>
          )}
        </div>
      ) : filteredAnlagen.length === 0 && hasActiveFilter ? (
        <div className="text-center py-8 text-gray-400">
          <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Keine Anlagen gefunden</p>
          <button onClick={() => { setSearchTerm(''); setStatusFilter(null); }}
            className="text-xs mt-1 text-primary hover:underline">
            Filter zurücksetzen
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredAnlagen.map(anlage => (
            <div key={anlage.id} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Anlage Header */}
              <div
                onClick={() => toggleExpand(anlage.id)}
                className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 transition"
              >
                {expanded[anlage.id] ? (
                  <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900 truncate">{anlage.bezeichnung}</span>
                    {anlage.typ && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 shrink-0">
                        {anlage.typ}
                      </span>
                    )}
                  </div>
                  {/* Wartungsstatus-Indikatoren */}
                  {anlage.wartungsVertraege?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {anlage.wartungsVertraege.map(v => {
                        const status = getWartungStatus(v);
                        return (
                          <span key={v.id} className={`text-xs flex items-center gap-1 ${status.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${status.dot || 'bg-gray-400'}`} />
                            {v.wartungsTyp}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Quick Info */}
                <div className="flex items-center gap-2 shrink-0">
                  {anlage._count?.wartungsHistorien > 0 && (
                    <span className="text-xs text-gray-400">{anlage._count.wartungsHistorien}x gewartet</span>
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {expanded[anlage.id] && (
                <div className="border-t border-gray-100 bg-gray-50/50 p-3 space-y-3">
                  {/* Edit-Form oder Details */}
                  {showAnlageForm === anlage.id ? (
                    <AnlageForm
                      initial={anlage}
                      onSave={handleSaveAnlage}
                      onCancel={() => setShowAnlageForm(false)}
                      saving={saving}
                    />
                  ) : (
                    <>
                      {/* Anlage Details */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-sm">
                        {anlage.hersteller && (
                          <div><span className="text-gray-500">Hersteller:</span> <span className="font-medium">{anlage.hersteller}</span></div>
                        )}
                        {anlage.modell && (
                          <div><span className="text-gray-500">Modell:</span> <span className="font-medium">{anlage.modell}</span></div>
                        )}
                        {anlage.baujahr && (
                          <div><span className="text-gray-500">Baujahr:</span> <span className="font-medium">{anlage.baujahr}</span></div>
                        )}
                        {anlage.seriennummer && (
                          <div><span className="text-gray-500">S/N:</span> <span className="font-medium">{anlage.seriennummer}</span></div>
                        )}
                        {anlage.standort && (
                          <div><span className="text-gray-500">Standort:</span> <span className="font-medium">{anlage.standort}</span></div>
                        )}
                      </div>
                      {anlage.notizen && (
                        <p className="text-xs text-gray-500 italic">{anlage.notizen}</p>
                      )}

                      {/* Aktions-Buttons */}
                      {!readOnly && (
                        <div className="flex gap-2">
                          <button onClick={() => setShowAnlageForm(anlage.id)}
                            className="text-xs flex items-center gap-1 px-2 py-1 text-gray-600 hover:bg-gray-200 rounded transition">
                            <Edit className="w-3 h-3" /> Bearbeiten
                          </button>
                          <button onClick={() => handleDeleteAnlage(anlage.id)}
                            className="text-xs flex items-center gap-1 px-2 py-1 text-red-600 hover:bg-red-50 rounded transition">
                            <Trash2 className="w-3 h-3" /> Entfernen
                          </button>
                        </div>
                      )}

                      {/* Wartungsverträge */}
                      <div className="border-t border-gray-200 pt-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Wartungsverträge</p>
                          {!readOnly && (
                            <button onClick={() => setShowVertragForm({ anlageId: anlage.id })}
                              className="text-xs flex items-center gap-1 text-primary hover:text-primary-dark transition">
                              <Plus className="w-3 h-3" /> Hinzufügen
                            </button>
                          )}
                        </div>

                        {anlage.wartungsVertraege?.length > 0 ? (
                          <div className="space-y-2">
                            {anlage.wartungsVertraege.map(vertrag => {
                              const status = getWartungStatus(vertrag);
                              const StatusIcon = status.icon;
                              return (
                                <div key={vertrag.id} className="bg-white rounded-lg p-2.5 border border-gray-100">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium">{vertrag.wartungsTyp}</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                                          vertrag.intervallTyp === 'GESETZLICH' ? 'bg-red-50 text-red-600' :
                                          vertrag.intervallTyp === 'EMPFOHLEN' ? 'bg-blue-50 text-blue-600' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>
                                          {INTERVALL_TYP_LABELS[vertrag.intervallTyp] || vertrag.intervallTyp}
                                        </span>
                                      </div>
                                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                                        <span>Alle {vertrag.intervallMonate} Monate</span>
                                        {vertrag.letzteWartung && <span>Letzte: {formatDatum(vertrag.letzteWartung)}</span>}
                                        {vertrag.katalogPosition && (
                                          <span className="text-gray-700 font-medium">
                                            {(vertrag.katalogPosition.verkaufspreis / 100).toFixed(2)} EUR
                                          </span>
                                        )}
                                      </div>
                                      <div className={`flex items-center gap-1 mt-1 text-xs ${status.color}`}>
                                        {StatusIcon && <StatusIcon className="w-3 h-3" />}
                                        {status.label}
                                      </div>
                                    </div>
                                    {!readOnly && (
                                      <div className="flex gap-1 shrink-0">
                                        <button onClick={() => setShowVertragForm({ anlageId: anlage.id, vertrag })}
                                          className="p-1 hover:bg-gray-100 rounded transition" title="Bearbeiten">
                                          <Edit className="w-3 h-3 text-gray-400" />
                                        </button>
                                        <button onClick={() => handleDeleteVertrag(vertrag.id)}
                                          className="p-1 hover:bg-red-50 rounded transition" title="Entfernen">
                                          <Trash2 className="w-3 h-3 text-gray-400" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-400 italic">Keine Wartungsverträge</p>
                        )}

                        {/* Vertrag-Formular */}
                        {showVertragForm?.anlageId === anlage.id && (
                          <WartungsVertragForm
                            anlageId={anlage.id}
                            initial={showVertragForm.vertrag}
                            onSave={handleSaveVertrag}
                            onCancel={() => setShowVertragForm(null)}
                            saving={saving}
                          />
                        )}
                      </div>

                      {/* Wartungshistorie */}
                      <div className="border-t border-gray-200 pt-3">
                        <button
                          onClick={() => loadHistorie(anlage.id)}
                          className="text-xs flex items-center gap-1 text-gray-600 hover:text-gray-900 transition"
                        >
                          {historieLoading === anlage.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <History className="w-3 h-3" />
                          )}
                          {showHistorie === anlage.id ? 'Historie ausblenden' : `Wartungshistorie anzeigen (${anlage._count?.wartungsHistorien || 0})`}
                        </button>

                        {showHistorie === anlage.id && historie[anlage.id] && (
                          <div className="mt-2 space-y-1.5">
                            {historie[anlage.id].length === 0 ? (
                              <p className="text-xs text-gray-400 italic">Keine Einträge</p>
                            ) : (
                              historie[anlage.id].map(eintrag => {
                                const ergebnis = ERGEBNIS_CONFIG[eintrag.ergebnis] || ERGEBNIS_CONFIG.OK;
                                return (
                                  <div key={eintrag.id} className="bg-white rounded p-2 border border-gray-100 text-xs">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <Calendar className="w-3 h-3 text-gray-400" />
                                        <span className="font-medium">{formatDatum(eintrag.datum)}</span>
                                        <span className={`px-1.5 py-0.5 rounded ${ergebnis.color}`}>
                                          {ergebnis.label}
                                        </span>
                                      </div>
                                      {eintrag.kosten != null && (
                                        <span className="text-gray-700 font-medium">{eintrag.kosten.toFixed(2)} EUR</span>
                                      )}
                                    </div>
                                    <p className="text-gray-600 mt-1">{eintrag.beschreibung}</p>
                                    {eintrag.notizen && <p className="text-gray-400 italic mt-0.5">{eintrag.notizen}</p>}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AnlagenSection;
